/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useMemo } from "react";
import MainLayout from "../../layouts/MainLayout";
import Form from "../../components/forms/Form";
import { useNavigate } from "react-router-dom";
import "../../assets/styles/custom-styles.css";
import { maskCep, removeMask } from "../../utils/maskUtils";
import { organizationFields } from "../../constants/forms/organizationFields";
import useLoader from "../../hooks/useLoader";
import useNotification from "../../hooks/useNotification";
import useForm from "../../hooks/useForm";
import colorToHex from "../../utils/colorToHex";
import {
  normalizeFormErrors,
  setDefaultFieldValues,
} from "../../utils/objectUtils";
import useBaseService from "../../hooks/services/useBaseService";
import { entities } from "../../constants/entities";
import PageHeader from "../../components/PageHeader";
import SimpleBody from "../../components/forms/SimpleBody";
import SectionHeader from "../../components/forms/SectionHeader";
import useRemoteSelectOptions from "../../hooks/useRemoteSelectOptions";
import useSelectOptions from "../../hooks/useSelectOptions";

const CreateOrganizationPage = () => {
  const navigate = useNavigate();
  const { showLoader, hideLoader } = useLoader();
  const { showNotification } = useNotification();
  const { formData, handleChange, setFormData, resetForm } = useForm(
    setDefaultFieldValues(organizationFields)
  );
  const { post: create, formErrors } = useBaseService(navigate);

  const handleFieldChange = useCallback(
    (fieldId, value, field) => {
      if (fieldId === "address.zip") {
        handleCepChange(value);
      } else if (fieldId === "organization.color") {
        handleColorChange(value);
      } else {
        handleChange(fieldId, value);
      }
    },
    // eslint-disable-next-line no-use-before-define
    [handleCepChange, handleChange, handleColorChange]
  );

  const handleColorChange = useCallback(
    (value) => {
      const hexColor = colorToHex(value);
      setFormData((prev) => ({
        ...prev,
        organization: {
          ...prev.organization,
          color: hexColor,
        },
      }));
    },
    [setFormData]
  );

  const stateSelect = useRemoteSelectOptions({
    queryKey: ["stateOptions"],
    fetcher: async () =>
      await fetch(`https://brasilapi.com.br/api/ibge/uf/v1`).then((res) =>
        res.json()
      ),
    fieldId: "address.state",
    formData,
    isMulti: false,
    mapToOption: (type) => ({ value: type.nome, label: type.nome }),
  });

  const rawCountryOptions = useMemo(
    () => [{ value: "Brasil", label: "Brasil" }],
    []
  );

  const countrySelect = useSelectOptions({
    fieldId: "address.country",
    formData,
    rawOptions: rawCountryOptions,
    isMulti: false,
    mapToOption: (item) => ({ value: item.value, label: item.label }),
  });

  const selects = {
    "address.state": stateSelect,
    "address.country": countrySelect,
  };

  // --- Funções para Selects (getOptions e getSelectedValue) ---
  const getOptions = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId];
      return selectConfig?.options || [];
    },
    [selects]
  );

  const getSelectedValue = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId];
      return selectConfig?.selectedValue || null;
    },
    [selects]
  );

  const handleCepChange = useCallback(
    async (cep) => {
      const value = maskCep(cep);
      const zip = removeMask(value);

      setFormData((prev) => ({
        ...prev,
        address: {
          ...prev.address,
          zip: value,
        },
      }));

      if (zip.length === 8) {
        showLoader();
        try {
          const response = await fetch(`https://viacep.com.br/ws/${zip}/json/`);
          if (!response.ok) throw new Error("Erro ao buscar o CEP");

          const data = await response.json();
          if (data.erro) throw new Error("CEP não encontrado");

          setFormData((prev) => ({
            ...prev,
            address: {
              ...prev.address,
              street: data.logradouro || "",
              district: data.bairro || "",
              city: data.localidade || "",
              state: data.estado || "",
              country: "Brasil" || "",
            },
          }));

          showNotification("success", "Endereço preenchido automaticamente!");
        } catch (error) {
          showNotification("error", error.message);
        } finally {
          hideLoader();
        }
      }
    },
    [setFormData, showNotification]
  );

  const handleSubmit = useCallback(async () => {
    const sanitizedData = {
      organization: {
        ...formData.organization,
      },
      address: {
        ...formData.address,
        zip: removeMask(formData.address.zip),
      },
      contact: {
        ...formData.contact,
      },
    };

    showLoader();
    try {
      const success = await create(
        entities.organizations.create,
        sanitizedData
      );
      if (success) {
        resetForm();
      }
    } catch (error) {
      console.log(error);
    } finally {
      hideLoader();
    }
  }, [formData, showLoader, hideLoader, showNotification, navigate]);

  const handleBack = useCallback(() => {
    navigate(`/aluguel/organizacoes/dashboard`);
  }, [navigate]);

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title="Cadastro de Organizações"
        showBackButton={true}
        backUrl="/aluguel/organizacoes/dashboard"
      />
      <div className="container-fluid p-1">
        <Form
          onSubmit={handleSubmit}
          initialFormData={formData}
          textSubmit="Cadastrar"
          textLoadingSubmit="Cadastrando..."
          handleBack={handleBack}
        >
          {() => {
            const renderedSections = new Set();

            return organizationFields.map((section, index) => {
              const sectionType = section.section.split(" - ")[0];
              const showHeader = !renderedSections.has(sectionType);

              if (showHeader) {
                renderedSections.add(sectionType);
              }

              return (
                <div key={section.section + " " + index}>
                  {showHeader && (
                    <SectionHeader
                      section={section.section}
                      viewTable={false}
                      setViewTable={() => {}}
                      addFieldsInData={() => {}}
                    />
                  )}

                  <SimpleBody
                    fields={section.fields}
                    formErrors={normalizeFormErrors(formErrors)}
                    formData={formData}
                    handleFieldChange={handleFieldChange}
                    getOptions={getOptions}
                    getSelectedValue={getSelectedValue}
                  />
                </div>
              );
            });
          }}
        </Form>
      </div>
    </MainLayout>
  );
};

export default CreateOrganizationPage;
